import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../services/web/bos-service.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subWeeks} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subWeeks(new Date(), 1),
    dtEnd: new Date(),
    statusBo: [],
    responsible: [],
    condition: [],
    reason: [],
    team: [],
    flagrant: null,
    neighborhood: [],
    street: []
});

const createConfig = (filterLabels) => ({
    url: "/bo_x_ray/apply",
    filterLabels,

    buildFilters: (filters, utils) => ({
        dtStart: filters.dtStart ? utils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? utils.formatDateTime(filters.dtEnd) : null,
        statusBo: filters.statusBo?.map(i => i.value) ?? [],
        team: filters.team?.map(i => i.value) ?? [],
        flagrant: filters.flagrant?.value ? [filters.flagrant.value] : []
    }),

    buildAuditFilters: (filters, formatted, utils) =>
        utils.filters({
            ...filters,
            dtStart: formatted.dtStart,
            dtEnd: formatted.dtEnd
        }, filterLabels, true),

    validatePeriod: (filters) => ReportUtils.periodValidate(filters.dtStart, filters.dtEnd)
});

const BoXray = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);

    const hasUrlFilters = () => {return ["dtStart", "dtEnd", "team"].some(param => ReportUtils.getUrlParam(param))};
    const [filters, setFilters] = useState(() => ReportUtils.buildUrlFilters({initialFilters, includeTeam: true}));

    const buttonRef = useRef(null);

    const { data: boName } = useQuery({
        queryKey: ['boName'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    const title = `Raio X de ${boName}`;
    const [headerSelected, setHeaderSelected] = useState(-1);

    const filterLabels = {
        dtStart: "Data/hora inicial",
        dtEnd: "Data/hora final",
        statusBo: [`Status do ${boName}`],
        responsible: "Responsável",
        condition: "Condição dos indivíduos",
        reason: "Natureza",
        team: "Equipe",
        flagrant: "Flagrante",
        neighborhood: "Bairro",
        street: "Logradouro"
    };

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const reportConfig = createConfig(filterLabels);

    useEffect(() => {
        if (!hasUrlFilters()) return;
        ReportUtils.fetchReport({currentFilters: filters, setLoading, setReportData, setCollapsed, ...reportConfig});
    }, []);

    const apply = async () => {
        if (hasUrlFilters()) await ReportUtils.fetchReport({currentFilters: filters, setLoading, setReportData, setCollapsed, ...reportConfig});

        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                statusBo: filters.statusBo?.map(i => i.value) ?? [],
                responsible: filters.responsible?.map(i => i.value) ?? [],
                condition: filters.condition?.map(i => i.value) ?? [],
                reason: filters.reason?.map(i => i.value) ?? [],
                team: filters.team?.map(i => i.value) ?? [],
                neighborhood: filters.neighborhood?.map(i => i.value) ?? [],
                street: filters.street?.map(i => i.value) ?? [],
                flagrant: filters.flagrant?.value ? [filters.flagrant.value] : []
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/bo_x_ray/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setLoading(false);
        }
    }

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        ReportUtils.clearUrlFilters();
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <div className={"flex"}>

            <Template isLoading={loading}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={reportData?.boCards[0].total}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                            setCollapsed={setCollapsed}
                        />
                    </div>
                </Template.Header>
                <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                    <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                          collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                        <Filters setFilters={setFilters} filters={filters} boName={boName}/>
                        <AutoDatetime setFilters={setFilters}/>

                    </Menu>
                </Template.Body>
            </Template>

            <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.boCards[0].total === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body>
                    <div className={`flex-1 ${!reportData || reportData?.boCards[0].total === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData}/>
                        <NoDataContent reportData={reportData} data={reportData?.boCards[0].total}/>
                        <Content reportData={reportData} data={reportData?.boCards[0].total} boName={boName} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
                    </div>
                </Card.Body>
            </Card>

        </div>
    );
};

export default BoXray;